import React, { useState, useEffect } from 'react';
import { Loader2, ShieldCheck, Smartphone, Mail, Copy, Download, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { authApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * Setting up your own two-factor authentication.
 *
 * DESIGN-signing-in.md decisions F and G1-G10. Its own file rather than another section of
 * ProfilePage.js, which is already 614 lines.
 *
 * The four steps:
 *   choose  -> pick an authenticator app or email
 *   verify  -> prove the method works (this is what actually enrols you)
 *   codes   -> the recovery codes, shown ONCE, behind an explicit confirmation
 *   done    -> the summary, with a way to switch it off
 *
 * ⚠️ THE `codes` STEP CARRIES DECISION G5 AND IS THE REASON THIS COMPONENT EXISTS.
 * Rob rejected admin-resets-admin deliberately, so one compromised admin account cannot
 * strip 2FA from a whole company. The cost of that lands here: recovery codes are the ONLY
 * self-service way back in. Somebody who skips past this screen and later loses their phone
 * has exactly one option left, and it is Rob personally. So "Finish setup" stays disabled
 * until they tick the box - deliberate friction, not clutter.
 *
 * ⚠️ FORCED MODE (G8-bis): with a `setupToken` prop, this same flow runs for somebody the
 * password door has BLOCKED because their grace period ran out. Same screens, different
 * plumbing — the token travels in each request instead of a session, and finishing calls
 * `onSignedIn(session)` with the real session the backend returns. ONE component on
 * purpose: a second copy of the enrolment flow would drift from this one, and the codes
 * step carries G5 in both.
 */
const TwoFactorSetup = ({ setupToken = null, onSignedIn = null, onExpired = null }) => {
  const forced = Boolean(setupToken);
  const [step, setStep] = useState(forced ? 'choose' : 'loading');
  const [method, setMethod] = useState(null);
  const [enrolledMethod, setEnrolledMethod] = useState(null);
  const [codesRemaining, setCodesRemaining] = useState(0);

  const [qrSvg, setQrSvg] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [saved, setSaved] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  // Forced mode: the real session comes back with the recovery codes, but is only USED
  // when they confirm they saved them — the codes screen must not be skippable by the
  // app simply moving on (G5).
  const [pendingSession, setPendingSession] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await authApi.twofactorStatus();
      setEnrolledMethod(data.enrolled ? data.method : null);
      setCodesRemaining(data.recovery_codes_remaining || 0);
      setStep(data.enrolled ? 'done' : 'choose');
    } catch (error) {
      // ⚠️ Say so rather than showing a "not set up" screen we cannot stand behind. A
      // failed read rendered as "off" would invite somebody to enrol a second time.
      toast.error("Couldn't load your security settings. Please refresh.");
      setStep('error');
    }
  };

  useEffect(() => {
    // ⚠️ No status call in forced mode — there is no session to ask with.
    if (!forced) loadStatus();
  }, []);

  // The set-up token lasts 30 minutes. When it dies, the only honest move is back to the
  // password screen — keeping somebody typing codes at a dead token helps nobody.
  const handleExpiredToken = (error) => {
    const detail = error.response?.data?.detail;
    if (forced && error.response?.status === 401 && typeof detail === 'string'
        && detail.includes('expired')) {
      toast.error(detail);
      if (onExpired) onExpired();
      return true;
    }
    return false;
  };

  const start = async (chosen) => {
    setBusy(true);
    try {
      const { data } = forced
        ? await authApi.twofactorSetupStart(setupToken, chosen)
        : await authApi.twofactorStart(chosen);
      setMethod(chosen);
      setQrSvg(data.qr_svg || null);
      setSecret(data.secret || null);
      setCode('');
      setStep('verify');
      if (chosen === 'email') toast.success('We have emailed you a code');
    } catch (error) {
      if (handleExpiredToken(error)) return;
      toast.error(error.response?.data?.detail || "Couldn't start setting that up.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    const entered = code.replace(/\D/g, '');
    if (entered.length !== 6) {
      toast.error('Enter the six digits');
      return;
    }
    setBusy(true);
    try {
      if (forced) {
        const { data } = await authApi.twofactorSetupConfirm(setupToken, entered);
        setPendingSession(data.session || null);
        setRecoveryCodes(data.recovery_codes || []);
      } else {
        const { data } = await authApi.twofactorConfirm(entered);
        setRecoveryCodes(data.recovery_codes || []);
      }
      setSaved(false);
      setStep('codes');
    } catch (error) {
      if (handleExpiredToken(error)) return;
      toast.error(error.response?.data?.detail || "That code isn't right.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!password) { toast.error('Enter your password to confirm'); return; }
    setBusy(true);
    try {
      await authApi.twofactorDisable(password);
      setPassword('');
      toast.success('Two-factor authentication switched off');
      await loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't switch it off.");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (!password) { toast.error('Enter your password to confirm'); return; }
    setBusy(true);
    try {
      const { data } = await authApi.twofactorRegenerateCodes(password);
      setPassword('');
      setRecoveryCodes(data.recovery_codes || []);
      setSaved(false);
      setStep('codes');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't create new codes.");
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.success('Copied');
  };

  const downloadCodes = () => {
    // ⚠️ A Blob, not a link to anywhere. These never travel over the network again.
    const blob = new Blob(
      [`HolidayHive recovery codes\n\nEach code works once. Keep these somewhere safe.\n\n${recoveryCodes.join('\n')}\n`],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'holidayhive-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (step === 'loading') {
    return <div className="flex items-center gap-2 text-muted-foreground py-4">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
    </div>;
  }

  if (step === 'error') {
    return <p className="text-sm text-muted-foreground py-4">
      Couldn't load your security settings. Please refresh the page.
    </p>;
  }

  if (step === 'choose') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Add a second step when you sign in, so a stolen password isn't enough on its own.
          You'll only be asked for one of these, not both.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => start('totp')}
            disabled={busy}
            className="text-left border rounded-lg p-4 hover:border-primary transition-colors disabled:opacity-60"
            data-testid="choose-totp"
          >
            <Smartphone className="w-5 h-5 mb-2 text-primary" />
            <p className="font-medium">Authenticator app</p>
            <p className="text-sm text-muted-foreground mt-1">
              Google Authenticator, Authy, 1Password. Works without a signal.
            </p>
          </button>
          <button
            type="button"
            onClick={() => start('email')}
            disabled={busy}
            className="text-left border rounded-lg p-4 hover:border-primary transition-colors disabled:opacity-60"
            data-testid="choose-email"
          >
            <Mail className="w-5 h-5 mb-2 text-primary" />
            <p className="font-medium">Email me a code</p>
            <p className="text-sm text-muted-foreground mt-1">
              A six-digit code each time you sign in. Nothing to install.
            </p>
            {/* ⚠️ "Email me a code" also names the sign-in-BY-code feature (decision H),
                which REPLACES a password and which admins may never use. Rob misread a
                real bug as a permissions rule because the two look identical. This line
                is the agreed fix — it marks this one as the second factor, not the
                first. See DESIGN-signing-in.md, the naming collision under H-ter. */}
            <p className="text-sm text-muted-foreground mt-1">
              You'll still need your password first.
            </p>
          </button>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <form onSubmit={confirm} className="space-y-4">
        {method === 'totp' ? (
          <>
            <p className="text-sm text-muted-foreground">
              Scan this with your authenticator app, then enter the six-digit code it shows.
            </p>
            {qrSvg && (
              <div
                className="inline-block bg-white p-2 rounded-lg border"
                /* The SVG comes from our own backend, not from user input. */
                dangerouslySetInnerHTML={{ __html: qrSvg }}
                data-testid="totp-qr"
              />
            )}
            {secret && (
              <p className="text-sm text-muted-foreground">
                Can't scan it? Enter this key by hand:{' '}
                <code className="font-mono bg-muted px-1.5 py-0.5 rounded">{secret}</code>
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            We've emailed you a six-digit code. Enter it below to finish.
          </p>
        )}

        <div className="space-y-2 max-w-xs">
          <Label htmlFor="enrol-code">Six-digit code</Label>
          <Input
            id="enrol-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="418207"
            className="text-center text-xl tracking-[0.4em] font-mono"
            data-testid="enrol-code-input"
          />
        </div>

        <div className="flex gap-2">
          <Button type="submit" className="btn-primary" disabled={busy} data-testid="enrol-confirm">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking...</> : 'Confirm'}
          </Button>
          <Button type="button" variant="outline" onClick={() => setStep('choose')} disabled={busy}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </form>
    );
  }

  if (step === 'codes') {
    return (
      <div className="space-y-4">
        <div>
          <p className="font-medium">Save your recovery codes</p>
          <p className="text-sm text-muted-foreground mt-1">
            If you lose your phone, these are how you get back in. Each one works once.
          </p>
        </div>

        <div className="bg-muted rounded-lg p-4 font-mono text-sm grid grid-cols-2 gap-x-6 gap-y-1"
             data-testid="recovery-codes">
          {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={downloadCodes}>
            <Download className="w-4 h-4 mr-1.5" /> Download
          </Button>
          <Button type="button" variant="outline" onClick={copyCodes}>
            <Copy className="w-4 h-4 mr-1.5" /> Copy
          </Button>
        </div>

        <div className="border border-destructive/40 bg-destructive/5 rounded-lg p-3">
          <p className="text-sm">
            You won't be shown these again. Without them, only HolidayHive support can get
            you back in.
          </p>
        </div>

        {/*
          ⚠️ THE TICK BOX IS THE POINT, NOT DECORATION. After decision G5 these codes are
          the only self-service way back in, so "Finish setup" stays disabled until somebody
          has actively said they have saved them. A download link people scroll past is not
          good enough when the fallback is a support ticket only Rob can close.
        */}
        <label className="flex items-start gap-2.5 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
            className="mt-0.5"
            data-testid="saved-checkbox"
          />
          <span>I've saved these somewhere safe</span>
        </label>

        <Button
          type="button"
          className="btn-primary"
          disabled={!saved}
          onClick={async () => {
            if (forced) {
              // The session has been waiting since /setup/confirm — it is only handed to
              // the app NOW, after the tick box, so the codes screen cannot be skipped.
              toast.success('Two-factor authentication is on');
              if (onSignedIn && pendingSession) onSignedIn(pendingSession);
              return;
            }
            await loadStatus();
            toast.success('Two-factor authentication is on');
          }}
          data-testid="finish-setup"
        >
          Finish setup
        </Button>
      </div>
    );
  }

  // step === 'done'
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2.5">
        <ShieldCheck className="w-5 h-5 text-green-600 mt-0.5" />
        <div>
          <p className="font-medium">Two-factor authentication is on</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {enrolledMethod === 'totp'
              ? "You'll be asked for a code from your authenticator app when you sign in."
              : "We'll email you a code when you sign in."}
          </p>
          <p className="text-sm text-muted-foreground mt-1" data-testid="codes-remaining">
            {codesRemaining} recovery {codesRemaining === 1 ? 'code' : 'codes'} left.
            {/* ⚠️ Running low is worth flagging BEFORE they run out — at zero the only
                remaining route is a support ticket only Rob can close (G5). */}
            {codesRemaining <= 2 && (
              <span className="text-destructive"> Create some more.</span>
            )}
          </p>
        </div>
      </div>

      <div className="space-y-2 max-w-xs">
        <Label htmlFor="twofactor-password">Confirm your password to change this</Label>
        <Input
          id="twofactor-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          data-testid="twofactor-password"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={regenerate} disabled={busy}>
          New recovery codes
        </Button>
        <Button type="button" variant="outline" onClick={disable} disabled={busy}
                data-testid="disable-2fa">
          Switch off
        </Button>
      </div>
    </div>
  );
};

export default TwoFactorSetup;
